(() => {
  'use strict';

  const ELIGIBLE_PRODUCT_TYPE = "Customizable Apparel";
  const CUSTOMIZATION_ITEM_PRODUCT_TYPE = "customization-item";
  const CUSTOMIZATION_FEE_PRODUCT_TYPE = "customization-fee";

  const UPLOAD_KEYS = ["front_img", "back_img", "sleeve_l_img", "sleeve_r_img"];

  const FEE_VARIANT_ID = {
    1: {
      "1-2 Color": 44767522553992,
      "Full Color 24–71": 44767373295752,
      "Full Color 72–143": 44767373328520,
      "Full Color 144–287": 44767373361288,
      "Full Color 288+": 44767373394056
    },
    2: {
      "1-2 Color": 44767522586760,
      "Full Color 24–71": 44767166595208,
      "Full Color 72–143": 44767373426824,
      "Full Color 144–287": 44767373459592,
      "Full Color 288+": 44767373492360
    },
    3: {
      "1-2 Color": 44767522619528,
      "Full Color 24–71": 44767373525128,
      "Full Color 72–143": 44767373557896,
      "Full Color 144–287": 44767373590664,
      "Full Color 288+": 44767373623432
    },
    4: {
      "1-2 Color": 44767522652296,
      "Full Color 24–71": 44767373656200,
      "Full Color 72–143": 44767373688968,
      "Full Color 144–287": 44767373721736,
      "Full Color 288+": 44767373754504
    }
  };
  const SCREENPRINT_FEE_VARIANT_IDS = new Set(
    Object.values(FEE_VARIANT_ID).flatMap((tierMap) => Object.values(tierMap))
  );

  let TB_QUEUE = Promise.resolve();
  let TB_LAST_CART = null;
  let TB_HOOKS_INSTALLED = false;

  function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  function enqueue(fn) {
    TB_QUEUE = TB_QUEUE.then(() => fn()).catch(console.error);
    return TB_QUEUE;
  }

  async function fetchCart() {
    await sleep(100);
    const res = await fetch("/cart.js", { credentials: "same-origin" });
    return res.json();
  }

  async function cartChange(lineKey, qty) {
    await fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({ id: lineKey, quantity: qty })
    });
  }

  async function cartAdd(variantId, qty, properties = {}) {
    await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: variantId, quantity: qty, properties })
    });
  }

  async function cartRemove(lineKey) {
    await cartChange(lineKey, 0);
  }

  function calcEligibleQty(cart) {
    return cart.items.reduce((sum, item) => {
      return sum + (item.product_type === ELIGIBLE_PRODUCT_TYPE ? item.quantity : 0);
    }, 0);
  }

  function countUploads(props = {}) {
    let count = 0;
    for (const key of UPLOAD_KEYS) {
      if (props[key] && String(props[key]).trim()) count++;
    }
    return Math.max(1, Math.min(4, count));
  }

  function getFullColorTier(qty) {
    if (qty >= 288) return "Full Color 288+";
    if (qty >= 144) return "Full Color 144–287";
    if (qty >= 72) return "Full Color 72–143";
    return "Full Color 24–71";
  }

  function publishCartUpdate() {
    if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: "tb-cart-lock" });
    }
  }

  function parseTs(props) {
    const v = props && props._tb_ts;
    const n = v ? Number(String(v)) : 0;
    return Number.isFinite(n) ? n : 0;
  }

  function pickNewestByTs(items) {
    let best = null;
    let bestTs = -1;
    let bestIdx = -1;
    items.forEach((it, idx) => {
      const ts = parseTs(it.properties);
      if (ts > bestTs || (ts === bestTs && idx > bestIdx)) {
        best = it;
        bestTs = ts;
        bestIdx = idx;
      }
    });
    return best;
  }

  function normalizeProductType(item) {
    return String(item && item.product_type ? item.product_type : "").trim().toLowerCase();
  }

  function dedupeKeysByTypeAndProductId(items, targetType) {
    const normalizedTarget = String(targetType || "").trim().toLowerCase();
    const groups = new Map();

    items.forEach((item) => {
      if (!item || normalizeProductType(item) !== normalizedTarget) return;
      const productId = String(item.product_id || "");
      if (!productId) return;
      if (!groups.has(productId)) groups.set(productId, []);
      groups.get(productId).push(item);
    });

    const removeKeys = [];
    groups.forEach((groupItems) => {
      if (groupItems.length < 2) return;
      const newest = pickNewestByTs(groupItems);
      groupItems.forEach((it) => {
        if (newest && it.key !== newest.key) removeKeys.push(it.key);
      });
    });

    return removeKeys;
  }

  function isCustomizationCartType(item) {
    const pt = normalizeProductType(item);
    return pt === CUSTOMIZATION_ITEM_PRODUCT_TYPE || pt === CUSTOMIZATION_FEE_PRODUCT_TYPE;
  }

  function isCustomizationItemType(item) {
    return normalizeProductType(item) === CUSTOMIZATION_ITEM_PRODUCT_TYPE;
  }

  function isCustomizationFeeType(item) {
    return normalizeProductType(item) === CUSTOMIZATION_FEE_PRODUCT_TYPE;
  }

  function isScreenprintFeeVariantId(variantId) {
    return SCREENPRINT_FEE_VARIANT_IDS.has(Number(variantId));
  }

  function formatPropertyLabel(key) {
    const map = {
      "front_img": "Front Image",
      "back_img": "Back Image",
      "sleeve_l_img": "Left Sleeve Image",
      "sleeve_r_img": "Right Sleeve Image",
      "front_img_location": "Front Placement",
      "back_img_location": "Back Placement",
      "color-select": "Color Selection",
      "Uploads": "Uploads",
      "Full Color": "Color Tier"
    };
    if (map[key]) return map[key];
    return key
      .replace(/[_\-]/g, " ")
      .replace(/\b\w/g, l => l.toUpperCase());
  }

  function findLineKeyFromRow(rowEl) {
    if (!rowEl) return null;

    const direct = rowEl.getAttribute("p-key") || rowEl.getAttribute("data-key");
    if (direct) return direct;

    const desc = rowEl.querySelector("[p-key]");
    if (desc && desc.getAttribute("p-key")) return desc.getAttribute("p-key");

    const desc2 = rowEl.querySelector("[data-key]");
    if (desc2 && desc2.getAttribute("data-key")) return desc2.getAttribute("data-key");

    return null;
  }

  function getRowRole(rowEl, cart) {
    const lineKey = findLineKeyFromRow(rowEl);
    if (!lineKey || !cart || !Array.isArray(cart.items)) return "";
    const item = cart.items.find((i) => i && i.key === lineKey);
    if (!item) return "";
    if (isCustomizationFeeType(item)) return "fee";
    if (isCustomizationItemType(item)) return "custom";
    return "";
  }

  /* =========================
     OVERLAY BRIDGE (SESSION-PERSISTENT)
     - Overlay UI/controller lives in theme.liquid.
     - This file keeps key handling + delegates show/hide calls to TB_CART_LOCK.overlay.
   ========================= */

  const TB_OVERLAY_KEYS = {
    pending: "tb_cart_op_pending",
    startedAt: "tb_cart_op_started_at",
    message: "tb_cart_op_message"
  };

  function tbGet(k) {
    try { return sessionStorage.getItem(k); } catch (_) { return null; }
  }

  function tbDel(k) {
    try { sessionStorage.removeItem(k); } catch (_) {}
  }

  function getOverlayApi() {
    try {
      const api = window.TB_CART_LOCK && window.TB_CART_LOCK.overlay;
      return api || null;
    } catch (_) {
      return null;
    }
  }

  function overlayIsPending() {
    const overlay = getOverlayApi();
    if (overlay && typeof overlay.isPending === "function") {
      return !!overlay.isPending();
    }
    return tbGet(TB_OVERLAY_KEYS.pending) === "1";
  }

  function overlayClearPending() {
    const overlay = getOverlayApi();
    if (overlay && typeof overlay.clearPending === "function") {
      overlay.clearPending();
      return;
    }
    tbDel(TB_OVERLAY_KEYS.pending);
    tbDel(TB_OVERLAY_KEYS.startedAt);
    tbDel(TB_OVERLAY_KEYS.message);
  }

  function showOverlay(message) {
    const overlay = getOverlayApi();
    if (overlay && typeof overlay.show === "function") {
      overlay.show(message);
    }
  }
  
  function hideOverlay() {
    const overlay = getOverlayApi();
    if (overlay && typeof overlay.hide === "function") {
      overlay.hide();
      return;
    }
    overlayClearPending();
  }

  /* CART STATE ENGINE */

  async function recalcCartState() {
    return enqueue(async () => {
      let cart = await fetchCart();
      TB_LAST_CART = cart;

      const dedupeKeys = [
        ...dedupeKeysByTypeAndProductId(cart.items, CUSTOMIZATION_ITEM_PRODUCT_TYPE),
        ...dedupeKeysByTypeAndProductId(cart.items, CUSTOMIZATION_FEE_PRODUCT_TYPE)
      ];

      if (dedupeKeys.length) {
        const uniqueKeys = [...new Set(dedupeKeys)];
        for (const key of uniqueKeys) {
          await cartRemove(key);
        }
        publishCartUpdate();
        cart = await fetchCart();
        TB_LAST_CART = cart;
      }

      const eligibleQty = calcEligibleQty(cart);

      // No eligible apparel => remove fee + customization
      if (eligibleQty === 0) {
        const removals = cart.items.filter(i => isCustomizationCartType(i));
        for (const item of removals) {
          await cartRemove(item.key);
        }
        publishCartUpdate();
        return;
      }

      const customItems = cart.items.filter(i => isCustomizationItemType(i));
      if (customItems.length === 0) return;

      const newestCustom = pickNewestByTs(customItems);
	  let removedCustomDupes = false;

	  for (const it of customItems) {
	    if (it.key !== newestCustom.key) {
	  	  removedCustomDupes = true;
		  await cartRemove(it.key);
	    }
	  }

	  if (removedCustomDupes) publishCartUpdate();

      const cart2 = await fetchCart();
      TB_LAST_CART = cart2;

      const customizationLines = cart2.items.filter(i => isCustomizationItemType(i));
      const customization = customizationLines.length ? pickNewestByTs(customizationLines) : null;
      if (!customization) return;

      const eligibleQtyNow = calcEligibleQty(cart2);
      const uploads = countUploads(customization.properties);

      const isFullColor = String(customization.properties["color-select"] || "")
        .toLowerCase()
        .includes("full");

      const tier = isFullColor ? getFullColorTier(eligibleQtyNow) : "1-2 Color";
      const desiredVariantId = (FEE_VARIANT_ID[uploads] || {})[tier];
      if (!desiredVariantId) return;

      const feeItems = cart2.items.filter(i =>
        isCustomizationFeeType(i) && isScreenprintFeeVariantId(i.variant_id)
      );
      const newestFee = feeItems.length ? pickNewestByTs(feeItems) : null;

      for (const it of feeItems) {
        if (newestFee && it.key !== newestFee.key) await cartRemove(it.key);
      }

      const cart3 = await fetchCart();
      TB_LAST_CART = cart3;

      const feeItems3 = cart3.items.filter(i =>
        isCustomizationFeeType(i) && isScreenprintFeeVariantId(i.variant_id)
      );
      const fee = feeItems3.length ? feeItems3[0] : null;

      const feeNeedsRebuild =
        !fee ||
        fee.variant_id !== desiredVariantId ||
        fee.quantity !== eligibleQtyNow;

      if (feeNeedsRebuild) {
        if (feeItems3.length) {
          for (const it of feeItems3) await cartRemove(it.key);
        }

        await cartAdd(desiredVariantId, eligibleQtyNow);
        publishCartUpdate();
      }
    });
  }

 /* =========================
   UI ENGINE (TRANSLATE)
========================= */


  function syncReturnToCustomizationButton(cart) {
    const btn = document.getElementById("tbReturnToCustomization");
    if (!btn) return;
  
    const hasCustomization = !!(cart && cart.items && cart.items.some(i => isCustomizationItemType(i)));

    if (!hasCustomization) {
      btn.style.display = "none";
      return;
    }

    // Force clean button layout
    btn.style.display = "flex";
    btn.style.alignItems = "center";
    btn.style.justifyContent = "center";
    btn.style.textAlign = "center";
    btn.style.width = "100%";
  }



function applyUiEnhancements() {
    if (TB_LAST_CART) syncReturnToCustomizationButton(TB_LAST_CART);
  if (!TB_LAST_CART) return;

  document.querySelectorAll(".cart-item").forEach(row => {
    const role = getRowRole(row, TB_LAST_CART);
    if (!role) return;

    // Translate dt labels
    row.querySelectorAll(".product-option dt").forEach(dt => {
      const raw = dt.textContent.replace(":", "").trim();
      if (!raw) return;
      dt.textContent = formatPropertyLabel(raw) + ":";
    });

    // Cleanup: remove any legacy fee-qty label if it exists
    const qtyCell = row.querySelector(".cart-item__quantity");
    if (qtyCell) {
      const label = qtyCell.querySelector(".tb-fee-qty");
      if (label) label.remove();
    }
  });
}

function applyStickyUi() {
  try {
    requestAnimationFrame(() => {
      applyUiEnhancements();
      requestAnimationFrame(() => applyUiEnhancements());
    });
  } catch (_) {}

  setTimeout(applyUiEnhancements, 0);
  setTimeout(applyUiEnhancements, 250);
  setTimeout(applyUiEnhancements, 900);
  setTimeout(applyUiEnhancements, 1600);
}

/* =========================
   DAWN HOOKS + INIT
========================= */

function initDawnHooks() {
  if (TB_HOOKS_INSTALLED) return;
  if (typeof subscribe !== "function" || typeof PUB_SUB_EVENTS === "undefined") return;

  TB_HOOKS_INSTALLED = true;

  subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
    if (event && event.source === "tb-cart-lock") {
      // CSS already hides controls; just re-run translations.
      applyStickyUi();
      return;
    }

    recalcCartState().then(() => {
      applyStickyUi();
    });
  });
}

async function initOnce() {
  initDawnHooks();


  // If a redirect flow flagged a pending cart operation, keep UI blocked until init completes.
  const TB_OVERLAY_PENDING = overlayIsPending();
  let TB_OVERLAY_TIMEOUT = null;
  if (TB_OVERLAY_PENDING) {
    showOverlay();
    TB_OVERLAY_TIMEOUT = setTimeout(() => {
      // Fail-open: never trap the customer behind an overlay.
      hideOverlay();
      overlayClearPending();
    }, 12000);
  }

  if (!TB_HOOKS_INSTALLED) {
    let tries = 0;
    const t = setInterval(() => {
      initDawnHooks();
      tries++;
      if (TB_HOOKS_INSTALLED || tries >= 20) clearInterval(t);
    }, 100);
  }

  TB_LAST_CART = await fetchCart();
  syncReturnToCustomizationButton(TB_LAST_CART);
    applyStickyUi();

  await recalcCartState();
  TB_LAST_CART = await fetchCart();
  syncReturnToCustomizationButton(TB_LAST_CART);
    applyStickyUi();

  if (TB_OVERLAY_TIMEOUT) clearTimeout(TB_OVERLAY_TIMEOUT);
  if (TB_OVERLAY_PENDING) {
    overlayClearPending();
    hideOverlay();
  }

}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    initOnce().catch(console.error);
  }, { once: true });
} else {
  initOnce().catch(console.error);
}

window.addEventListener("pageshow", (e) => {
  if (e && e.persisted) {
    initOnce().catch(console.error);
  } else {
    setTimeout(() => { applyStickyUi(); }, 100);
    setTimeout(() => { applyStickyUi(); }, 400);
    setTimeout(() => { applyStickyUi(); }, 1200);
  }
});

window.TB_CART_LOCK = window.TB_CART_LOCK || {};
window.TB_CART_LOCK.recalc = () => recalcCartState();
window.TB_CART_LOCK.apply = () => applyStickyUi();

})();

